import { useState, useEffect, useContext } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline'
import { equipmentMedia } from 'functions/equipmentUtils'
import { WhichWeightAPI } from 'WhichWeightAPI'
import { resetActiveWorkoutPreviewState } from 'redux/slices/activeWorkoutPreview'
import { initializeGroupActiveWorkout, initializeIndividualActiveWorkout } from 'redux/slices/activeWorkout'
import { useGetExercises } from 'redux/customHooks/useGetExercises'
import { ModalKey, ModalContext } from 'components/Modals/ModalContext'

import ScreenFrame from 'components/Frame/ScreenFrame'
import SolidButton from 'components/Buttons/SolidButton'
import BrandColorButton from 'components/Buttons/BrandColorButton'
import GroupAttendance from 'components/ActiveWorkoutPreview/GroupAttendance'
import UserProfilePicture from 'components/User/UserProfilePicture'
import WorkoutLength from 'components/ActiveWorkoutPreview/WorkoutLength'
import WorkoutPreview from 'components/WorkoutPreview'

export default function ActiveWorkoutPreview() {
    const navigate = useNavigate()
    const dispatch = useDispatch()
    
    const { exercises } = useGetExercises()
    const { ShowModal } = useContext(ModalContext)

    const coachId = useSelector((state) => state.user.id)
    const profileType = useSelector((state) => state.activeWorkoutPreview.profileType)
    const groupId = useSelector((state) => state.activeWorkoutPreview.groupId)
    const groupName = useSelector((state) => state.activeWorkoutPreview.groupName)
    const athleteId = useSelector((state) => state.activeWorkoutPreview.athleteId)
    const weekOfTheYearKey = useSelector((state) => state.activeWorkoutPreview.weekOfTheYearKey)
    const workoutTemplateKey = useSelector((state) => state.activeWorkoutPreview.workoutTemplateKey)
    const taperData = useSelector((state) => state.activeWorkoutPreview.taperData)
    const programPhaseKey = useSelector((state) => state.activeWorkoutPreview.programPhaseKey)
    const programPhaseWeekIndex = useSelector((state) => state.activeWorkoutPreview.programPhaseWeekIndex)

    /*
    const taperData = {
        progressionLevelRegression: 0,
        taperDampener: 0.8,
        taperLevel: 'taper-one',
        taperType: 'program',
        useTaper: true
    }
    */

    const [loadingWorkoutTemplate, setLoadingWorkoutTemplate] = useState(true)
    const [individualAthleteDetails, setIndividualAthleteDetails] = useState(null)
    const [groupAthleteAttendanceData, setGroupAthleteAttendanceData] = useState([])
    const [selectedWorkoutLength, setSelectedWorkoutLength] = useState(30)
    const [workoutTemplate, setWorkoutTemplate] = useState(null)
    const [isStartWorkoutLoading, setIsStartWorkoutLoading] = useState(false)

    const FetchWorkoutTemplateFromTheBackend = async () => {

        setLoadingWorkoutTemplate(true)
        setWorkoutTemplate(null)

        var defaultWorkoutLength = 60
        var workoutTemplate = null

        if (profileType === 'group') {

            const jsonBody = {
                groupId: groupId,
                weekOfTheYearKey: weekOfTheYearKey,
                workoutTemplateKey: workoutTemplateKey
            }
    
            // console.log(jsonBody)
    
            const result = await WhichWeightAPI('PROGRAM', 'GET', '/group_workout_preview_data', jsonBody)
    
            // console.log(result.data.data)
    
            if (result.data.status === 'failure') {
                console.error(result)
                setWorkoutTemplate(null)
                setLoadingWorkoutTemplate(false)
                return
            }

            var groupAthleteAttendanceData = JSON.parse(JSON.stringify(result.data.data.groupAthleteAttendanceData))

            // If an athlete id was passed in with the group id we can assume this is a group individual workout
            // Update attendance box to default to the one athlete

            if (athleteId !== null) {
                for (let i = 0; i < groupAthleteAttendanceData.length; i++) {

                    // Set all other present athletes to absent
                    if (
                        parseInt(groupAthleteAttendanceData[i].id) !== parseInt(athleteId)
                        && groupAthleteAttendanceData[i].attendanceStatus === 'present'
                    ) {
                        groupAthleteAttendanceData[i].attendanceStatus = 'absent'
                    }

                    // Set the one athlete to present if not completed (shouldn't hit this case)
                    if (
                        parseInt(groupAthleteAttendanceData[i].id) === parseInt(athleteId)
                        && groupAthleteAttendanceData[i].attendanceStatus !== 'complete'
                    ) {
                        groupAthleteAttendanceData[i].attendanceStatus = 'present'
                    }
                }
            }

            workoutTemplate = result.data.data.workoutTemplate
            defaultWorkoutLength = result.data.data.defaultWorkoutLength
            setGroupAthleteAttendanceData(groupAthleteAttendanceData)
        }

        if (profileType === 'individual') {

            const jsonBody = {
                userId: athleteId,
                weekOfTheYearKey: weekOfTheYearKey,
                workoutTemplateKey: workoutTemplateKey,
            }
    
            // console.log(jsonBody)
    
            const result = await WhichWeightAPI('PROGRAM', 'GET', '/individual_workout_preview_data', jsonBody)
    
            // console.log(result.data.data)
    
            if (result.data.status === 'failure') {
                setWorkoutTemplate(null)
                setLoadingWorkoutTemplate(false)
                return
            }

            workoutTemplate = result.data.data.workoutTemplate
            defaultWorkoutLength = result.data.data.defaultWorkoutLength
    
            const individualAthleteDetails = {
                athleteId: athleteId,
                firstName: result.data.data.firstName,
                lastName: result.data.data.lastName,
            }
    
            setIndividualAthleteDetails(individualAthleteDetails)

        }

        // Set default workout length

        if (typeof defaultWorkoutLength === 'number' && [30, 40, 60].includes(defaultWorkoutLength)) {
            setSelectedWorkoutLength(defaultWorkoutLength)
        }

        // Process workout template with exercise data

        var processedWorkoutTemplate = JSON.parse(JSON.stringify(workoutTemplate))

        processedWorkoutTemplate?.phases?.map((phase) => {

            phase.exercises.map((exercise) => {

                // Add sport for phase level UI expand/collapse

                phase.expanded = true

                // Add spot to store the original exercise in the event of a swap

                exercise.originalExerciseStorage = null

                // Cache the exercise video URL
                
                const exerciseData = exercises.find((item) => item.id === exercise.id)

                if (exerciseData) {
                    exercise.videoUrl = exerciseData.videoUrl
                }

            })
        })

        // console.log(processedWorkoutTemplate)

        setWorkoutTemplate(processedWorkoutTemplate)
        setLoadingWorkoutTemplate(false)

    }

    // Header Actions

    const OnBackButton = () => {
        dispatch(resetActiveWorkoutPreviewState())
        navigate(-1)
    }

    const OnStartWorkoutButton = () => {
        if (profileType === 'group') {
            StartGroupWorkout()
        }

        if (profileType === 'individual') {
            StartIndividualWorkout()
        }
    }

    const StartGroupWorkout = async () => {

        const presentGroupAthletes = groupAthleteAttendanceData.filter((athlete) => athlete.attendanceStatus === 'present')

        if (presentGroupAthletes.length === 0) {
            // TODO: Add modal popup
            console.error('No present group athletes found')
            return
        }

        const presentGroupAthleteIds = presentGroupAthletes.map((athlete) => athlete.id)

        // console.log(presentGroupAthletes)
        // console.log(presentGroupAthleteIds)

        setIsStartWorkoutLoading(true)

        const jsonBody = {
            groupId: groupId,
            trainerId: coachId,
            groupAthleteIds: presentGroupAthleteIds,
            name: workoutTemplate.name,
            uiMode: 'coach-dashboard'
        }

        // console.log(jsonBody)

        const result = await WhichWeightAPI('REPORTING', 'POST', '/group_workout', jsonBody)

        // console.log(result)

        setIsStartWorkoutLoading(false)

        if (result.data.status == 'failure') {
            console.error(result)
            return
        }

        // Check for workout ID

        const workoutId = result?.data?.data

        if (workoutId === null) {
            console.error(result)
            return
        }

        // Final proccessing of workout template

        const lengthFilteredWorkoutTemplate = FilterWorkoutTemplateByWorkoutLength(workoutTemplate)
        const finalWorkoutTemplate = InitializeGroupWorkoutTemplateSetData(presentGroupAthletes, lengthFilteredWorkoutTemplate)

        // Initialize active workout state

        const activeWorkoutPayload = {
            profileType: profileType,
            id: parseInt(workoutId),
            groupId: groupId,
            groupName: groupName,
            groupAthletes: presentGroupAthletes,
            weekOfTheYearKey: weekOfTheYearKey,
            programPhaseKey: programPhaseKey,
            programPhaseWeekIndex: programPhaseWeekIndex,
            workoutType: 'program',
            workoutTemplateKey: workoutTemplateKey,
            taperData: taperData,
            workoutTemplate: finalWorkoutTemplate
        }

        dispatch(initializeGroupActiveWorkout(activeWorkoutPayload))

        navigate(`/active/workout/${profileType}/${groupId}`)

    }

    const StartIndividualWorkout = async () => {

        setIsStartWorkoutLoading(true)

        const jsonBody = {
            trainerId: coachId,
            traineeId: athleteId,
            name: workoutTemplate.name,
            uiMode: 'coach-dashboard'
        }

        // console.log(jsonBody)

        const result = await WhichWeightAPI('CORE', 'POST', '/workout', jsonBody)

        // console.log(result)

        setIsStartWorkoutLoading(false)

        if (result.data.status == 'failure') {
            console.error(result)
            return
        }

        // Check for workout ID

        const workoutId = result?.data?.data

        if (workoutId === null) {
            console.error(result)
            return
        }

        // Final setup of workout template

        const lengthFilteredWorkoutTemplate = FilterWorkoutTemplateByWorkoutLength(workoutTemplate)
        const finalWorkoutTemplate = InitializeIndividualWorkoutTemplateSetData(lengthFilteredWorkoutTemplate)
        
        // Initialize active workout state

        const activeWorkoutPayload = {
            profileType: profileType,
            id: parseInt(workoutId),
            athleteId: parseInt(athleteId),
            athleteName: individualAthleteDetails.firstName + ' ' + individualAthleteDetails.lastName,
            weekOfTheYearKey: weekOfTheYearKey,
            programPhaseKey: programPhaseKey,
            programPhaseWeekIndex: programPhaseWeekIndex,
            workoutType: 'program',
            workoutTemplateKey: workoutTemplateKey,
            taperData: taperData,
            workoutTemplate: finalWorkoutTemplate
        }

        dispatch(initializeIndividualActiveWorkout(activeWorkoutPayload))

        navigate(`/active/workout/${profileType}/${athleteId}`)

    }

    // Workout template modification functions

    const ShowWorkoutLengthSelectionModal = () => {
        const modalData = {
            key: ModalKey.WorkoutLengthSelection,
            fullscreen: false,
            size: 'quarter',
            profileType: profileType,
            groupId: groupId,
            athleteId: athleteId,
            selectedWorkoutLength: selectedWorkoutLength,
            setSelectedWorkoutLength: setSelectedWorkoutLength
        }

        ShowModal(modalData)
    }

    const FilterWorkoutTemplateByWorkoutLength = (paramterWorkoutTemplate) => {

        const lengthFilteredWorkoutTemplate = JSON.parse(JSON.stringify(paramterWorkoutTemplate))

        // Store selected workout length

        lengthFilteredWorkoutTemplate.workoutLength = selectedWorkoutLength

        // Filter JSON by workout length if applicable

        if (selectedWorkoutLength < 60) {

            // For each phase
            
            lengthFilteredWorkoutTemplate.phases.map((phase) => {

                // Filter by those with supported length

                phase.exercises = phase.exercises.filter((exercise) => exercise.supportedWorkoutLengths.includes(selectedWorkoutLength))

            })

            // Get rid of any phases that no longer have exercises

            lengthFilteredWorkoutTemplate.phases = lengthFilteredWorkoutTemplate.phases.filter((phase) => phase.exercises.length > 0)
        }

        // Filter out any missing exercises

        lengthFilteredWorkoutTemplate.phases.map((phase) => {
            phase.exercises = phase.exercises.filter((exercise) => exercise.id !== -1)
        })

        // console.log(lengthFilteredWorkoutTemplate)

        return lengthFilteredWorkoutTemplate
    }

    const GetRequiredEquipment = (reduxWorkoutTemplate) => {
        var requiredEquipment = []

        // For each phase
        reduxWorkoutTemplate.phases.map((phase) => {
            // For each exercise
            phase.exercises.map((exercise) => {
                // Edge case for band and machine equipment. Only push band as required
                if (exercise.equipment === 'band,machine') {
                    if (!requiredEquipment.includes('band')) requiredEquipment.push('band')
                }
                // Edge case for dumbbell and kettlebell equipment. Only push dumbbell as required
                else if (exercise.equipment === 'dumbbell,kettlebell') {
                    if (!requiredEquipment.includes('dumbbell')) requiredEquipment.push('dumbbell')
                }
                // Edge case for band, dumbbell, and kettlebell equipment. Only push dumbbell as required
                else if (exercise.equipment === 'band,dumbbell,kettlebell') {
                    if (!requiredEquipment.includes('band')) requiredEquipment.push('band')
                    if (!requiredEquipment.includes('dumbbell')) requiredEquipment.push('dumbbell')
                } else {
                    var currentExerciseEquipment = exercise.equipment.split(',')

                    currentExerciseEquipment.map((equipment) => {
                        if (equipment.length > 0) {
                            if (!requiredEquipment.includes(equipment)) requiredEquipment.push(equipment)
                        }
                    })
                }
            })
        })

        return requiredEquipment
    }

    const SwapWorkoutPreviewExercise = (phaseIndex, exerciseIndex, swapType, swapExercise) => {
        var updatedWorkoutTemplate = JSON.parse(JSON.stringify(workoutTemplate))
        var currentExercise = updatedWorkoutTemplate.phases[phaseIndex].exercises[exerciseIndex];

        // Save current exercise to original exercise storage
        const storageExercise = {
            id: currentExercise.id,
            name: currentExercise.name,
            exerciseType: currentExercise.exerciseType,
            lateral: currentExercise.lateral,
            setUnit: currentExercise.setUnit,
            videoUrl: currentExercise.videoUrl,
            generationVariables: currentExercise.generationVariables,
            equipment: currentExercise.equipment
        }

        currentExercise.originalExerciseStorage = storageExercise

        // Update exercise in workout template
        currentExercise.id = swapExercise.id;
        currentExercise.name = swapExercise.name;
        currentExercise.exerciseType = swapExercise.exerciseType;
        currentExercise.lateral = swapExercise.lateral;
        currentExercise.setUnit = swapExercise.setUnit;
        currentExercise.videoUrl = swapExercise.videoUrl;
        currentExercise.generationVariables = { fetchType: swapType }
        currentExercise.equipment = swapExercise.equipment

        setWorkoutTemplate(updatedWorkoutTemplate)
    }

    const UndoWorkoutPreviewExerciseSwap = (phaseIndex, exerciseIndex) => {
        const updatedWorkoutTemplate = JSON.parse(JSON.stringify(workoutTemplate))
        var currentExercise = updatedWorkoutTemplate.phases[phaseIndex].exercises[exerciseIndex];

        // Update exercise
        currentExercise.id = currentExercise.originalExerciseStorage.id;
        currentExercise.name = currentExercise.originalExerciseStorage.name;
        currentExercise.exerciseType = currentExercise.originalExerciseStorage.exerciseType;
        currentExercise.lateral = currentExercise.originalExerciseStorage.lateral;
        currentExercise.setUnit = currentExercise.originalExerciseStorage.setUnit;
        currentExercise.videoUrl = currentExercise.originalExerciseStorage.videoUrl;
        currentExercise.generationVariables = currentExercise.originalExerciseStorage.generationVariables
        currentExercise.equipment = currentExercise.originalExerciseStorage.equipment

        // Clear stored exercise
        currentExercise.originalExerciseStorage = null

        setWorkoutTemplate(updatedWorkoutTemplate)
    }

    const InitializeGroupWorkoutTemplateSetData = (parameterGroupAthletes, paramterWorkoutTemplate) => {

        var processedWorkoutTemplate = JSON.parse(JSON.stringify(paramterWorkoutTemplate))

        processedWorkoutTemplate.phases.map((phase) => {

            phase.exercises.map((exercise) => {

                exercise.sets.map((set) => {

                    // Initialize recommendation data

                    set.recommendationData = {
                        weightRecommendationLoaded: false,
                        weightRecommendation: 0,
                        weightRecommendationRounded: 0,
                        labelWeightRecommendationRounded: 0,
                        formulaTag: '',
                        equipmentDetails: null
                    }

                    // Initialize completed set data

                    var setUnitsCompleted = 0
                    if (exercise.setUnit === 'reps') setUnitsCompleted = set.reps
                    if (exercise.setUnit === 'seconds') setUnitsCompleted = set.seconds

                    set.completedSetData = {
                        savingResult: false,
                        completed: false,
                        completedTimestamp: null,
                        skipped: false,
                        setUnitsCompleted: setUnitsCompleted,
                        movementDifficulty: 0,
                        difficulty: 0,
                        override: 0,
                        databaseId: 0
                    }

                    // Initialize group-individual athlete specific data

                    set.groupIndividualAthleteDetails = {}
                    set.groupIndividualRecommendationData = {}
                    set.groupIndividualCompletedSetData = {}

                    parameterGroupAthletes.map((athlete) => {

                        // Initialize athlete details

                        set.groupIndividualAthleteDetails[athlete.id] = {
                            firstName: athlete.firstName,
                            lastName: athlete.lastName,
                            groupIndividualOverrideInputs: false
                        }

                        // Initialize athlete specific recommendationData

                        set.groupIndividualRecommendationData[athlete.id] = {
                            weightRecommendationLoaded: false,
                            weightRecommendation: 0,
                            weightRecommendationRounded: 0,
                            labelWeightRecommendationRounded: 0,
                            formulaTag: '',
                            equipmentDetails: null
                        }

                        // Initialize athlete specific completedSetData

                        set.groupIndividualCompletedSetData[athlete.id] = {
                            savingResult: false,
                            completed: false,
                            completedTimestamp: null,
                            skipped: false,
                            setUnitsCompleted: setUnitsCompleted,
                            movementDifficulty: 0,
                            difficulty: 0,
                            override: 0,
                            databaseId: 0
                        }

                    })
                })

            })

        })

        return processedWorkoutTemplate

    }

    const InitializeIndividualWorkoutTemplateSetData = (paramterWorkoutTemplate) => {

        var processedWorkoutTemplate = JSON.parse(JSON.stringify(paramterWorkoutTemplate))

        processedWorkoutTemplate.phases.map((phase) => {

            phase.exercises.map((exercise) => {

                exercise.sets.map((set) => {

                    // Initialize recommendationData
                    
                    set.recommendationData = {
                        weightRecommendationLoaded: false,
                        weightRecommendation: 0,
                        weightRecommendationRounded: 0,
                        labelWeightRecommendationRounded: 0,
                        formulaTag: '',
                        equipmentDetails: null,
                    }

                    // Initialize completedSetData

                    var setUnitsCompleted = 0
                    if (exercise.setUnit === 'reps') setUnitsCompleted = set.reps
                    if (exercise.setUnit === 'seconds') setUnitsCompleted = set.seconds

                    set.completedSetData = {
                        savingResult: false,
                        completed: false,
                        completedTimestamp: null,
                        skipped: false,
                        setUnitsCompleted: setUnitsCompleted,
                        movementDifficulty: 0,
                        difficulty: 0,
                        override: 0,
                        databaseId: 0,
                    }

                })

            })

        })

        return processedWorkoutTemplate

    }

    useEffect(() => { FetchWorkoutTemplateFromTheBackend() }, [profileType, groupId, athleteId, weekOfTheYearKey, workoutTemplateKey])

    if (exercises.length === 0 || profileType === null || loadingWorkoutTemplate) {
        return (
            <ScreenFrame alwaysHideResumeWorkoutHeader={true}>
                <div className='space-y-4'>
                    <div className='w-full h-20 bg-gray-200 rounded-lg animate-pulse' />

                    <div className='grid grid-cols-2 gap-4'>
                        <div className='w-full h-20 bg-gray-200 rounded-lg animate-pulse' />
                        <div className='w-full h-20 bg-gray-200 rounded-lg animate-pulse' />
                    </div>

                    <div className='grid grid-cols-2 gap-4'>
                        <div className='w-full h-20 bg-gray-200 rounded-lg animate-pulse' />
                        <div className='w-full h-20 bg-gray-200 rounded-lg animate-pulse' />
                    </div>

                    <div className='grid grid-cols-2 gap-4'>
                        <div className='w-full h-20 bg-gray-200 rounded-lg animate-pulse' />
                        <div className='w-full h-20 bg-gray-200 rounded-lg animate-pulse' />
                    </div>
                </div>
            </ScreenFrame>
        )
    }

    if (workoutTemplate === null) {
        return (
            <ScreenFrame alwaysHideResumeWorkoutHeader={true}>
                <ExclamationTriangleIcon className="mx-auto h-16 text-orange-400" />

                <p className="text-gray-600 text-center">
                    There was an error grabbing the workout template. Please try again.
                </p>
            </ScreenFrame>
        )
    }

    return (
        <ScreenFrame alwaysHideResumeWorkoutHeader={true}>
            <div className="flex items-center mb-6 gap-x-4">
                <h1 className="flex-1 min-w-0">
                    <p className='text-3xl font-bold max-w-[55vw] 2xl:max-w-[50vw] truncate'>
                        {profileType === 'group' && <>{groupName} - </>}
                        {workoutTemplate.name}
                    </p>
                </h1>

                <div className='w-40'>
                    <SolidButton
                        size="large"
                        text="Back"
                        action={OnBackButton}
                        className='shadow-md'
                    />
                </div>

                <div className='w-40'>
                    <BrandColorButton
                        size="large"
                        text="Start"
                        action={OnStartWorkoutButton}
                        isLoading={isStartWorkoutLoading}
                        className='shadow-md'
                    />
                </div>
            </div>

            <div className='flex gap-x-12'>
                <div className='flex-1 flex flex-col space-y-8'>

                    {/* Group Left Side - Just Attendance */}

                    {profileType === 'group' && groupAthleteAttendanceData.length > 0 && (
                        <GroupAttendance 
                            groupAthleteAttendanceData={groupAthleteAttendanceData} 
                            setGroupAthleteAttendanceData={setGroupAthleteAttendanceData}
                        />
                    )}

                    {/* Individual Left Side - Athlete, Workout Length, and Equipment */}

                    {profileType === 'individual' && GetRequiredEquipment(FilterWorkoutTemplateByWorkoutLength(workoutTemplate)).length > 0 && (
                        <>
                            {individualAthleteDetails && 
                                <>
                                    <p>Athlete</p>

                                    <div className='flex items-center rounded-xl overflow-hidden text-center bg-white p-4 gap-x-4 shadow-md'>
                                        <UserProfilePicture 
                                            firstName={individualAthleteDetails.firstName} 
                                            lastName={individualAthleteDetails.lastName} 
                                            shadow={false}
                                        />

                                        <p className='text-lg'>
                                            {individualAthleteDetails.firstName} {individualAthleteDetails.lastName}
                                        </p>
                                    </div>
                                </>
                            }
                            
                            {workoutTemplateKey.includes('strength') && (
                                <WorkoutLength 
                                    selectedWorkoutLength={selectedWorkoutLength} 
                                    ShowWorkoutLengthSelectionModal={ShowWorkoutLengthSelectionModal} 
                                />
                            )}

                            <p>Equipment</p>

                            <div className='grid grid-cols-2 gap-4'>
                                {GetRequiredEquipment(FilterWorkoutTemplateByWorkoutLength(workoutTemplate)).map((equipment, index) => (
                                    <div
                                        key={index}
                                        className='px-4 py-2 flex items-center rounded-xl overflow-hidden text-center bg-white gap-x-2 shadow-md'
                                    >
                                        <img
                                            src={equipmentMedia[equipment].imageURL}
                                            alt="Equipment Icon"
                                            className="h-6 m-1"
                                        />

                                        <p className="text-gray-500">{equipmentMedia[equipment].name}</p>
                                    </div>
                                ))}
                            </div>
                        </>
                    )}
                </div>

                <div className="flex-1 flex flex-col mb-8 space-y-4">

                    {/* Group Right Side - Workout Length and Equipment */}

                    {profileType === 'group' && GetRequiredEquipment(FilterWorkoutTemplateByWorkoutLength(workoutTemplate)).length > 0 && (
                        <>
                             {workoutTemplateKey.includes('strength') && (
                                <WorkoutLength 
                                    selectedWorkoutLength={selectedWorkoutLength} 
                                    ShowWorkoutLengthSelectionModal={ShowWorkoutLengthSelectionModal} 
                                />
                            )}

                            <p>Equipment</p>

                            <div className='grid grid-cols-2 gap-4'>
                                {GetRequiredEquipment(FilterWorkoutTemplateByWorkoutLength(workoutTemplate)).map((equipment, index) => (
                                    <div
                                        key={index}
                                        className='px-4 py-2 flex items-center rounded-xl overflow-hidden text-center bg-white gap-x-2 shadow-md'
                                    >
                                        <img
                                            src={equipmentMedia[equipment].imageURL}
                                            alt="Equipment Icon"
                                            className="h-6 m-1"
                                        />

                                        <p className="text-gray-500">{equipmentMedia[equipment].name}</p>
                                    </div>
                                ))}
                            </div>
                        </>
                    )}

                    {/* Workout Preview */}

                    <p>Exercises</p>

                    <WorkoutPreview
                        workoutType='program'
                        workoutTemplate={FilterWorkoutTemplateByWorkoutLength(workoutTemplate)}
                        SwapWorkoutPreviewExercise={SwapWorkoutPreviewExercise}
                        UndoWorkoutPreviewExerciseSwap={UndoWorkoutPreviewExerciseSwap}
                    />
                </div>
            </div>
        </ScreenFrame>
    )
}
